import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { KPIs } from "@/hooks/useAttributionData";

function formatMoney(cents: number): string {
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
  return `$${dollars.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

function KPICard({ label, value, sub, tooltip, color, loading }: { label: string; value: string; sub?: string; tooltip?: string; color: string; loading: boolean }) {
  return (
    <VentureCard hover={false} className="flex-1 min-w-0">
      <VentureCardContent className="pt-3 pb-3 text-center">
        <p className="text-[10px] font-semibold tracking-wider text-muted-foreground mb-1 uppercase truncate">{label}</p>
        {loading ? <Skeleton className="h-7 w-14 mx-auto mb-1" /> : <p className={`text-2xl font-bold ${color}`}>{value}</p>}
        {sub && (
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {sub}
            {tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent><p>{tooltip}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </p>
        )}
      </VentureCardContent>
    </VentureCard>
  );
}

export function AttributionKPIStrip({ kpis, loading }: { kpis: KPIs; loading: boolean }) {
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
      <KPICard label="Ad Spend" value={formatMoney(kpis.totalSpendCents)} sub={`${kpis.totalImpressions.toLocaleString()} impressions`} color="text-foreground" loading={loading} />
      <KPICard label="Leads" value={kpis.totalConversions > 0 ? kpis.totalConversions.toLocaleString() : kpis.totalLeads.toLocaleString()} sub={kpis.costPerLeadCents > 0 ? `${formatMoney(kpis.costPerLeadCents)} CPL` : "no spend data"} tooltip="Cost per lead from ad platforms" color="text-blue-400" loading={loading} />
      <KPICard label="Demos Booked" value={kpis.demosBooked.toLocaleString()} sub={kpis.costPerDemoCents > 0 ? `${formatMoney(kpis.costPerDemoCents)} per demo` : `${kpis.showRate}% show rate`} tooltip="Cost per booked demo" color="text-cyan-400" loading={loading} />
      <KPICard label="Demos Closed" value={kpis.demosClosed.toLocaleString()} sub={`${kpis.closeRate}% close rate`} tooltip="Percentage of showed demos that closed" color="text-violet-400" loading={loading} />
      <KPICard label="Revenue" value={formatMoney(kpis.totalRevenueCents)} sub="from closed deals" color="text-emerald-400" loading={loading} />
      <KPICard label="ROAS" value={kpis.overallROAS !== null ? `${kpis.overallROAS}x` : "—"} sub={kpis.overallCTR > 0 ? `${kpis.overallCTR}% CTR` : ""} tooltip="Return on ad spend (revenue / spend)" color={kpis.overallROAS !== null && kpis.overallROAS >= 3 ? "text-emerald-400" : kpis.overallROAS !== null && kpis.overallROAS >= 2 ? "text-yellow-400" : "text-muted-foreground"} loading={loading} />
    </div>
  );
}
